import { FC } from 'react';

import { Grid } from '@mui/material';
import { AddPartnerApplication } from '@prisma/client';

import getPostalLabel from 'src/lib/postalLabel';

import { AddressFields, ContactFields } from './components';
import PartnerInformationSummary from './components/PartnerInformationSummary/PartnerInformationSummary';
import { SectionHeader } from './components/SectionHeader';

export type PartnerInformationSummaryProps = {
    postalLabel: string;
    formSummaryData?: AddPartnerApplication;
};

const PartnerAddressSummary = ({
    formSummaryData,
    postalLabel,
}: PartnerInformationSummaryProps) => {
    return (
        <>
            <SectionHeader title="Address Information" />

            <Grid item xs={12} spacing={3} container>
                <AddressFields
                    heading="Registered Address"
                    postalLabel={postalLabel}
                    eircode={formSummaryData?.ai_ra_eircode}
                    addressLine1={formSummaryData?.ai_ra_addressLine1}
                    addressLine2={formSummaryData?.ai_ra_addressLine2}
                    city={formSummaryData?.ai_ra_city}
                    county={formSummaryData?.ai_ra_county}
                    country={formSummaryData?.ai_ra_country}
                />

                <AddressFields
                    heading="Trading Address"
                    postalLabel={postalLabel}
                    eircode={formSummaryData?.ai_ta_eircode}
                    addressLine1={formSummaryData?.ai_ta_addressLine1}
                    addressLine2={formSummaryData?.ai_ta_addressLine2}
                    city={formSummaryData?.ai_ta_city}
                    county={formSummaryData?.ai_ta_county}
                    country={formSummaryData?.ai_ta_country}
                    gridSx={{ marginTop: '24px' }}
                />
            </Grid>
        </>
    );
};

const PartnerContactInformationSummary = ({
    formSummaryData,
    postalLabel,
}: PartnerInformationSummaryProps) => {
    return (
        <>
            <SectionHeader title="Primary Contact Information" />

            <Grid item xs={12} spacing={3} container>
                <ContactFields
                    firstName={formSummaryData?.pci_firstName}
                    lastName={formSummaryData?.pci_lastName}
                    email={formSummaryData?.pci_email}
                    telephoneNumber={formSummaryData?.pci_telephoneNumber}
                    mobilePhoneNumber={formSummaryData?.pci_mobilePhoneNumber}
                    position={formSummaryData?.pci_position}
                />

                <AddressFields
                    heading="Registered Address"
                    postalLabel={postalLabel}
                    eircode={formSummaryData?.pci_ra_eircode}
                    addressLine1={formSummaryData?.pci_ra_addressLine1}
                    addressLine2={formSummaryData?.pci_ra_addressLine2}
                    city={formSummaryData?.pci_ra_city}
                    county={formSummaryData?.pci_ra_county}
                    country={formSummaryData?.pci_ra_country}
                    gridSx={{ marginTop: '40px' }}
                />
            </Grid>
        </>
    );
};

const BillingContactInformationSummary = ({
    formSummaryData,
    postalLabel,
}: PartnerInformationSummaryProps) => {
    return (
        <>
            <SectionHeader title="Billing Contact Information" />

            <Grid item xs={12} spacing={3} container>
                <ContactFields
                    firstName={formSummaryData?.bci_firstName}
                    lastName={formSummaryData?.bci_lastName}
                    email={formSummaryData?.bci_email}
                    telephoneNumber={formSummaryData?.bci_telephoneNumber}
                    mobilePhoneNumber={formSummaryData?.bci_mobilePhoneNumber}
                    position={formSummaryData?.bci_position}
                />

                <AddressFields
                    heading="Registered Address"
                    postalLabel={postalLabel}
                    eircode={formSummaryData?.bci_ra_eircode}
                    addressLine1={formSummaryData?.bci_ra_addressLine1}
                    addressLine2={formSummaryData?.bci_ra_addressLine2}
                    city={formSummaryData?.bci_ra_city}
                    country={formSummaryData?.bci_ra_country}
                    gridSx={{ marginTop: '40px' }}
                />
            </Grid>
        </>
    );
};

const ServiceContactInformationSummary = ({
    formSummaryData,
    postalLabel,
}: PartnerInformationSummaryProps) => {
    return (
        <>
            <SectionHeader title=" Service Contact Information" />

            <Grid item xs={12} spacing={3} container>
                <ContactFields
                    firstName={formSummaryData?.sci_firstName}
                    lastName={formSummaryData?.sci_lastName}
                    email={formSummaryData?.sci_email}
                    telephoneNumber={formSummaryData?.sci_telephoneNumber}
                    mobilePhoneNumber={formSummaryData?.sci_mobilePhoneNumber}
                    position={formSummaryData?.sci_position}
                />

                <AddressFields
                    heading="Registered Address"
                    postalLabel={postalLabel}
                    eircode={formSummaryData?.sci_ra_eircode}
                    addressLine1={formSummaryData?.sci_ra_addressLine1}
                    addressLine2={formSummaryData?.sci_ra_addressLine2}
                    city={formSummaryData?.sci_ra_city}
                    country={formSummaryData?.sci_ra_country}
                    gridSx={{ marginTop: '40px' }}
                />
            </Grid>
        </>
    );
};

const PartnerInformation: FC<PartnerInformationSummaryProps> = ({ formSummaryData }) => {
    const postalLabel = getPostalLabel(formSummaryData?.countryDetails?.alpha2Code);

    return (
        <Grid item xs={12} sx={{ padding: '32px 24px' }} gap={3} container>
            {/* Partner Information */}
            <PartnerInformationSummary
                formSummaryData={formSummaryData}
                postalLabel={postalLabel}
            />

            {/* Partner Address */}
            <PartnerAddressSummary formSummaryData={formSummaryData} postalLabel={postalLabel} />

            {/* Partner Contact Information */}
            <PartnerContactInformationSummary
                formSummaryData={formSummaryData}
                postalLabel={postalLabel}
            />

            {/* Billing Contact Information */}
            <BillingContactInformationSummary
                formSummaryData={formSummaryData}
                postalLabel={postalLabel}
            />

            {/* Service Contact Information */}
            <ServiceContactInformationSummary
                formSummaryData={formSummaryData}
                postalLabel={postalLabel}
            />
        </Grid>
    );
};

export default PartnerInformation;
